/* eslint-disable react/no-array-index-key */
import React from 'react';
import {configure} from 'mobx';
import initializeStore from 'env/stores/stores';
import config from 'env/config';
import {
  GetStaticPropsResult,
  GetStaticPropsContext,
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  NextPageContext,
} from 'next';
import NexudusAppStore from 'env/stores/appStore';
import Agent from 'env/agent';
import {LOCATION_CHOICE_COOKIE_NAME, TOKEN_NAME} from 'env/stores/authStore';
import {
  getApolloStateProps,
  initializeApollo,
} from './runway-east/DatoApolloProvider';
import {getBusinesssOrNestedBusinessesFromBusinessByIds} from './runway-east/book-a-space/useBookingFilters';

interface CustomPage {
  Name: string;
  source?: string;
}

configure({
  enforceActions: 'observed', // strict mode
});

const fetchSourceCodeFromPagePaths = async (pages: CustomPage[]) => {
  const jsxPages = pages.filter((p) => p.Name.indexOf('.jsx') > -1);

  const fetchFuncs = jsxPages.map((p) => async () => {
    const jsxPage = p;
    const pagesHost = config.apiRoot;
    const url = `${pagesHost}/en/page/${jsxPage.Name}?raw=true`;
    const res = await fetch(url);

    const source = await res.text();
    jsxPage.source = source;
  });

  const fetchFuncsPromises: Promise<void>[][] = [];

  while (fetchFuncs.length) {
    fetchFuncsPromises.push(fetchFuncs.splice(0, 25).map((f) => f()));
  }
  await Promise.all(fetchFuncsPromises);
};

interface GetMobxStoreOptions {
  storeLoaders?: (mobxStore: {appStore: NexudusAppStore}) => Promise<void>[];
  host?: string | null;
  cookieString?: string;
  stores?: any;
}

const getMobxStore = async ({
  host,
  storeLoaders,
  cookieString,
  stores,
}: GetMobxStoreOptions) => {
  const mobxStore = initializeStore({stores, host, cookieString});

  if (storeLoaders) {
    await Promise.all(storeLoaders(mobxStore));
  }

  return mobxStore;
};

let localMobxStore: any;

export function getInitialPropsForAppWrapper(
  additionalStoreLoader?: (mobxStore: any) => Array<Promise<any>>,
): (pageContext: NextPageContext) => Promise<any> {
  return async (pageContext: NextPageContext): Promise<any> => {
    const cookieString =
      typeof window === 'undefined'
        ? pageContext.req?.headers.cookie
        : document.cookie;
    const mobxStore =
      typeof window !== 'undefined' && localMobxStore
        ? localMobxStore
        : await getMobxStore({
            host:
              cookieString &&
              // eslint-disable-next-line @typescript-eslint/no-use-before-define
              (await getHostFromCookieOrUserDefault(cookieString)),
            cookieString,
            storeLoaders: (m) => [
              m.appStore.loadConfiguration(),
              m.appStore.loadBusiness(),
              m.appStore.loadBusinesses(true),
              ...(additionalStoreLoader ? additionalStoreLoader(m) : []),
            ],
          });

    localMobxStore = mobxStore;

    const apolloClient = initializeApollo(
      null,
      !!(pageContext.req as any)?.preview,
    );

    const apolloStateProps = getApolloStateProps(apolloClient);
    const mobxStoreJson = JSON.parse(JSON.stringify(mobxStore));

    const defaultProps = {...apolloStateProps, mobxStore: mobxStoreJson};

    return {
      ...defaultProps,
      isLoadedViaGetInitialProps: true,
    };
  };
}

export function getStaticPropsForAppWrapper<
  Props extends Record<string, any> = Record<string, any>,
  Query extends {[key: string]: string | string[] | undefined} = {
    [key: string]: string | string[] | undefined;
  }
>(
  additionalStoreLoader?: (mobxStore: any) => Array<Promise<any>>,
  props?:
    | GetStaticPropsResult<Record<string, unknown>>
    | ((
        context: GetStaticPropsContext<Query>,
        apolloClient: ReturnType<typeof initializeApollo>,
        mobxStore: {appStore: AppStore},
      ) => Promise<GetStaticPropsResult<Record<string, unknown>>>),
) {
  return async (
    context: GetStaticPropsContext<Query>,
  ): Promise<GetStaticPropsResult<Props>> => {
    const mobxStore = await getMobxStore({
      storeLoaders: (m) => [
        m.appStore.loadConfiguration(),
        m.appStore.loadBusiness(),
        m.appStore.loadBusinesses(true),
        ...(additionalStoreLoader ? additionalStoreLoader(m) : []),
      ],
    });

    const apolloClient = initializeApollo(null, !!context.preview);

    const resolvedProps =
      props && typeof props === 'function'
        ? await props(context, apolloClient, mobxStore)
        : props;

    const apolloStateProps = getApolloStateProps(apolloClient);
    const mobxStoreJson = JSON.parse(JSON.stringify(mobxStore));

    const defaultProps = {...apolloStateProps, mobxStore: mobxStoreJson};

    if (resolvedProps && 'props' in resolvedProps) {
      return {
        revalidate: 120,
        ...resolvedProps,
        props: {
          ...resolvedProps.props,
          ...defaultProps,
        } as any,
      };
    }

    if (resolvedProps && 'redirect' in resolvedProps) {
      return {
        revalidate: 120,
        ...resolvedProps,
      };
    }

    if (resolvedProps && resolvedProps.notFound) {
      return {notFound: true};
    }

    return {
      revalidate: 120,
      props: {
        ...defaultProps,
      } as any,
    };
  };
}

async function getHostFromCookieOrUserDefault(cookieString: string) {
  // console.log('Getting Host from Cookie');
  // const {Auth, Business, cookies} = new Agent(null, cookieString, true);
  // const currentLocationId = cookies.get(LOCATION_CHOICE_COOKIE_NAME);
  // console.log('currentLocationId', currentLocationId);

  // try {
  //   const business = await Business.getBusiness();

  //   const businessId =
  //     parseInt(currentLocationId, 10) ||
  //     (cookies.get(TOKEN_NAME) ? (await Auth.getMe()).DefaultBusinessId : null);

  //   console.log('businessId', businessId);

  //   if (!businessId) return null;

  //   const defaultBusiness = getBusinesssOrNestedBusinessesFromBusinessByIds(
  //     [businessId],
  //     business,
  //   );
  //   console.log('defaultBusiness', defaultBusiness);

  //   const [{NativeHomeUrl: host}] = defaultBusiness;
  //   console.log('host', host);

  //   return host;
  // } catch (e) {
  //   console.warn('e', e);
  //   // Probably not logged in
  // }
  return null;
}

function isClientSideRouting(context: GetServerSidePropsContext) {
  return context.req.url && context.req.url.endsWith('.json');
}

export function getServerSidePropsForAppWrapper<
  Props extends Record<string, any> = Record<string, any>,
  Query extends {[key: string]: string | string[] | undefined} = {
    [key: string]: string | string[] | undefined;
  }
>(opts?: {
  additionalStoreLoader?: (mobxStore: any) => Array<Promise<any>>;
  omitLoaders?: ('configuration' | 'business' | 'businesses' | 'pages')[];
}) {
  return async (
    context: GetServerSidePropsContext<Query>,
  ): Promise<GetServerSidePropsResult<Props>> => {
    const apolloClient = initializeApollo(null, !!context.preview);

    if (isClientSideRouting(context)) return {props: {}} as any;

    const {cookie: cookieString} = context.req.headers;

    const host = cookieString
      ? await getHostFromCookieOrUserDefault(cookieString)
      : null;

    const mobxStore = await getMobxStore({
      host,
      cookieString,
      storeLoaders: (m) => [
        ...(opts?.omitLoaders?.includes('configuration')
          ? []
          : [m.appStore.loadConfiguration()]),
        ...(opts?.omitLoaders?.includes('business')
          ? []
          : [m.appStore.loadBusiness()]),
        ...(opts?.omitLoaders?.includes('businesses')
          ? []
          : [m.appStore.loadBusinesses(true)]),
        ...(opts?.omitLoaders?.includes('pages')
          ? []
          : [
              m.appStore
                .loadCustomPages()
                .then(async (loadedCustomPages: []) => {
                  if (m.appStore.hasLoadedCustomPages) return;

                  await fetchSourceCodeFromPagePaths(loadedCustomPages);

                  m.appStore.setCustomPages([...loadedCustomPages]);
                }),
            ]),
        ...(opts?.additionalStoreLoader ? opts.additionalStoreLoader(m) : []),
      ],
    });

    const apolloStateProps = getApolloStateProps(apolloClient);
    const mobxStoreJson = JSON.parse(JSON.stringify(mobxStore));

    const defaultProps = {
      ...apolloStateProps,
      mobxStore: mobxStoreJson,
      host: host || null,
      cookieString: cookieString || null,
    };

    return {
      props: {
        ...defaultProps,
      } as any,
    };
  };
}

export interface Business {
  Id: number;
  Name: string;
  HomeUrl: string;
  Businesses: Business[];
  NativeHomeUrl: string;
  RootBusinessId?: number;
}
export interface AppStore {
  loadHomePage: () => any;
  virtualDashboard: boolean;
  business: Business;
  businessesTree: Business[];
  businesses: Business[];
  configuration: {
    'PublicWebSite.Home.UseCommunityDashboard': string;
    'Website.CustomHead': string;
    'Website.CustomJS': string;
    PrimaryWebColor: string;
  };
  homePage: {
    Gallery: any;
  };
}

export interface AuthStore {
  isLoggedIn: boolean;
  accessToken: string | undefined;
  customer: Record<string, string> | undefined;
  registrableDomain: string | undefined;
  loadMe: () => Promise<any>;
  loadCustomer: () => Promise<any>;
}

export interface CheckoutStore {
  basket: any[];
}

export interface BookingsStore {
  resourceGroups: {groupName: string | undefined}[];
  resourceTypes: {Id: string; Name: string}[];
  fromTime: Date;
  toTime: Date;
  shiftId: string;
  shifts: {id: string; title: string}[];
}

export const withAppWrapper = (additionalStoreLoaders?: () => Array<any>) => (
  Component: ((props: any) => JSX.Element) | React.ComponentType<any>,
): ((props: any) => JSX.Element) => {
  const ComponentWithAppWrapper = (props: Record<any, any>) => (
    <Component {...props} />
  );

  ComponentWithAppWrapper.getInitialProps = getInitialPropsForAppWrapper(
    additionalStoreLoaders,
  );

  return ComponentWithAppWrapper;
};
